<template>
  <div class="page-tenants__expanded">
    <div class="page-tenants__expanded-columns">
      <dl class="page-tenants__facts">
        <dt>{{ $t('page.admin-subscriptions.detail.customer') }}</dt>
        <dd>{{ item.stripeCustomerId || '—' }}</dd>
        <dt>{{ $t('page.admin-subscriptions.detail.email') }}</dt>
        <dd>{{ item.customerEmail || '—' }}</dd>
        <dt>{{ $t('page.admin-tenants.data.trial-start') }}</dt>
        <dd>{{ item.trialStartDate ? formatStandardDate(item.trialStartDate) : '—' }}</dd>
        <dt>{{ $t('page.admin-tenants.data.trial-end') }}</dt>
        <dd>
          {{ item.trialEndDate ? formatStandardDate(item.trialEndDate) : '—' }}
          <span v-if="countdown" :class="countdown.tone">({{ countdown.text }})</span>
        </dd>
        <dt>{{ $t('page.admin-subscriptions.detail.synced') }}</dt>
        <dd>{{ item.syncedAt ? formatStandardDate(item.syncedAt) : '—' }}</dd>
      </dl>

      <div class="page-tenants__users">
        <div class="page-tenants__users-header">
          <span class="page-tenants__users-title">
            {{ $t('page.admin-tenants.client-emails') }} ({{ emails.length }})
          </span>
          <BaseButton
            isSmall
            isSquare
            isGrey
            has-icon-left
            :disabled="!emails.length"
            @click.native="$emit('copy-emails')"
          >
            <BaseIcon name="copy" />
            {{ $t('page.admin-tenants.copy-all-emails') }}
          </BaseButton>
        </div>
        <ul v-if="emails.length" class="page-tenants__email-list">
          <li v-for="email in emails" :key="email">{{ email }}</li>
        </ul>
        <p v-else class="page-tenants__expanded-empty">
          {{ $t('page.admin-tenants.no-external-emails') }}
        </p>
      </div>
    </div>

    <div v-if="!item.billingUnavailable" class="page-tenants__actions">
      <BaseButton v-if="!item.linked" isSmall isSquare has-icon-left @click.native="$emit('link')">
        <BaseIcon name="link" />
        {{ $t('page.admin-subscriptions.actions.link') }}
      </BaseButton>

      <BaseButton
        v-if="item.linked"
        isSmall
        isSquare
        isGrey
        has-icon-left
        :isLoading="busy === 'resync'"
        :disabled="!!busy"
        @click.native="$emit('resync')"
      >
        <BaseIcon name="refresh-cw" />
        {{ $t('page.admin-subscriptions.actions.resync') }}
      </BaseButton>

      <BaseButton
        v-if="item.linked"
        isSmall
        isSquare
        isGrey
        has-icon-left
        :disabled="!!busy"
        @click.native="$emit('unlink')"
      >
        <BaseIcon name="link-2" />
        {{ $t('page.admin-subscriptions.actions.unlink') }}
      </BaseButton>

      <BaseButton isSmall isSquare isGrey has-icon-right @click.native="$emit('payment-link')">
        {{ $t('page.admin-subscriptions.actions.payment-link') }}
        <BaseIcon name="copy" />
      </BaseButton>

      <BaseButton v-if="item.linked" isSmall isSquare isGrey has-icon-right targetBlank :href="stripeDashboardUrl">
        {{ $t('page.admin-subscriptions.actions.open-stripe') }}
        <BaseIcon name="external-link" />
      </BaseButton>
    </div>

    <p v-if="item.paymentIssue && item.latestInvoiceUrl" class="page-tenants__invoice">
      <a :href="item.latestInvoiceUrl" target="_blank" rel="noopener noreferrer">
        {{ $t('page.admin-subscriptions.detail.unpaid-invoice') }}
      </a>
    </p>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import { formatStandardDate } from '@/util/date-util';

// What an account holds beyond its row (Comptes): its Stripe customer, trial dates, the client's
// emails and the billing actions. Under its row on larger screens, in its sheet on phones; the
// page runs the actions.
export default {
  name: 'TenantBillingDetails',
  components: { BaseButton, BaseIcon },
  props: {
    item: { type: Object, required: true },
    // The client's own emails, not Aloha's.
    emails: { type: Array, default: () => [] },
    // The Stripe action running on the account, if any ('resync', 'unlink').
    busy: { type: String, default: null },
    // Days left on a running trial: `{ text, tone }`.
    countdown: { type: Object, default: null },
  },
  computed: {
    stripeDashboardUrl() {
      return `https://dashboard.stripe.com/customers/${this.item.stripeCustomerId}`;
    },
  },
  methods: {
    formatStandardDate,
  },
};
</script>

<style lang="scss">
.page-tenants {
  &__expanded {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 8px;
  }

  &__expanded-columns {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 64px;
    align-items: flex-start;
  }

  &__facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px 16px;
    margin: 0;
    font-size: 13px;

    dt {
      opacity: 0.6;
    }

    dd {
      margin: 0;
      font-family: monospace;
    }
  }

  &__users {
    min-width: 280px;
  }

  &__users-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  &__users-title {
    font-size: 13px;
    opacity: 0.6;
  }

  &__email-list {
    margin: 0;
    padding-left: 20px;
    color: $color-normal;
    font-size: 13px;
    line-height: 1.6;
  }

  &__expanded-empty {
    margin: 0;
    color: $color-normal-pale;
    font-size: 13px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__invoice {
    margin: 0;
    font-size: 13px;
  }

  // Phones, in the account's sheet: one column, the actions one under the other, 44px each.
  @include screen-below('tablet') {
    &__expanded {
      padding: 0;
    }

    &__expanded-columns {
      flex-direction: column;
      gap: 16px;
    }

    &__facts dd {
      overflow-wrap: anywhere;
    }

    &__users {
      min-width: 0;
    }

    &__users-header {
      flex-wrap: wrap;

      .base-button__element {
        min-height: 44px;
      }
    }

    &__actions {
      flex-direction: column;
      align-items: stretch;

      .base-button__element {
        width: 100%;
        min-height: 44px;
      }
    }
  }
}
</style>
